import { useEffect, useRef, useState } from "react";
import { ImageUp, Monitor, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { logoUrl, refreshBranding, useBranding } from "@/lib/branding";

const FIELDS = [["app_name", "Nome del pannello"], ["manager_name", "Nome del gestore"], ["company_name", "Azienda a cui fanno riferimento i dati"], ["welcome_title", "Titolo di benvenuto"]];

const LogoBox = ({ b, kind, label }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = logoUrl(b, kind);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post(`/branding/logo/${kind}`, fd); refreshBranding(data); toast.success("Logo caricato"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const remove = async () => { const { data } = await api.delete(`/branding/logo/${kind}`); refreshBranding(data); };
  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid={`logo-box-${kind}`}>
      <div className="mb-2 text-xs font-semibold text-slate-700">{label}</div>
      <div className="grid h-20 place-items-center rounded-md bg-slate-50">{src ? <img src={src} alt={label} className="max-h-16 max-w-[180px] object-contain" /> : <span className="text-xs text-slate-400">Nessun logo</span>}</div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`logo-input-${kind}`} onChange={(e) => upload(e.target.files[0])} />
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" disabled={busy} data-testid={`logo-upload-${kind}`} onClick={() => ref.current.click()}><ImageUp className="mr-1.5 h-3.5 w-3.5" />{busy ? "Caricamento..." : "Carica"}</Button>
        {src && <Button size="sm" variant="ghost" data-testid={`logo-remove-${kind}`} onClick={remove}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
      </div>
      <p className="mt-1 text-[11px] text-slate-400">PNG, JPG o WEBP, max 2 MB</p>
    </div>
  );
};

const Glow = ({ color }) => <><div className="absolute -left-10 -top-10 h-32 w-32 rounded-full bg-blue-600/30 blur-2xl" /><div className="absolute -bottom-10 right-0 h-32 w-32 rounded-full opacity-40 blur-2xl" style={{ background: color }} /></>;
const Logo = ({ h }) => <div className="relative inline-block"><div className="absolute -inset-3 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.9),transparent)] blur-md" /><img src="/nouvelle-crm-logo.png" alt="" className={`relative ${h} w-auto`} /></div>;
const Company = ({ f, b }) => (logoUrl(b, "company") || f.company_name) ? (
  <div className="flex items-center gap-2" data-testid="preview-company"><span className="text-[8px] uppercase tracking-widest text-slate-500">Dati di</span>
    {logoUrl(b, "company") && <img src={logoUrl(b, "company")} alt="" className="h-5 w-auto rounded bg-white p-0.5" />}<span className="text-[10px] text-slate-200">{f.company_name}</span></div>) : null;
const FakeForm = ({ f }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4">
    <div className="text-[13px] font-semibold text-white">Accedi a {f.app_name}</div>
    {["Email", "Password"].map((l) => <div key={l} className="mt-3"><div className="text-[8px] font-semibold uppercase text-slate-400">{l}</div><div className="mt-1 h-7 rounded-lg border border-slate-800 bg-slate-950/70" /></div>)}
    <div className="mt-4 h-7 rounded-lg bg-gradient-to-r from-blue-600 via-teal-600 to-emerald-500" />
  </div>
);
const PhonePreview = ({ f, b }) => (
  <div className="flex justify-center p-5" data-testid="preview-phone-frame">
    <div className="relative h-[540px] w-[270px] overflow-hidden rounded-[38px] border-[7px] border-slate-800 bg-[#07090e] shadow-xl">
      <div className="absolute left-1/2 top-1.5 z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-slate-800" />
      <Glow color={f.color} />
      <div className="relative flex h-full flex-col gap-5 overflow-hidden px-4 pt-10">
        <Logo h="h-20" />
        <FakeForm f={f} />
        <div className="border-t border-white/5 pt-3"><Company f={f} b={b} /></div>
      </div>
    </div>
  </div>
);
const DesktopPreview = ({ f, b }) => (
  <div className="relative m-4 overflow-hidden rounded-lg bg-[#07090e] p-5">
    <Glow color={f.color} />
    <div className="relative grid grid-cols-2 items-center gap-4">
      <div className="space-y-3"><Logo h="h-16" /><div className="font-heading text-sm font-semibold leading-tight text-white">{f.welcome_title}</div><p className="text-[10px] text-slate-400">{f.welcome_text}</p><Company f={f} b={b} /></div>
      <FakeForm f={f} />
    </div>
  </div>
);

export const BrandingTab = () => {
  const b = useBranding();
  const [f, setF] = useState(null);
  const [view, setView] = useState("phone");
  useEffect(() => { if (!f && !b._fallback) setF(b); }, [b]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const save = async () => {
    try { const { data } = await api.put("/branding", f); refreshBranding(data); toast.success("Landing page aggiornata"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <Panel title="Personalizzazione" testId="branding-panel">
        <div className="grid gap-3 p-5 sm:grid-cols-2">
          {FIELDS.map(([k, l]) => <label key={k} className="text-xs text-slate-500">{l}<Input className="mt-1" data-testid={`branding-${k}-input`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>)}
          <label className="text-xs text-slate-500 sm:col-span-2">Testo di benvenuto<Textarea className="mt-1" rows={3} data-testid="branding-welcome_text-input" value={f.welcome_text || ""} onChange={(e) => setF({ ...f, welcome_text: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Colore principale
            <div className="mt-1 flex items-center gap-2"><input type="color" data-testid="branding-color-input" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200" /><span className="font-mono text-xs">{f.color}</span></div></label>
          <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
            <LogoBox b={b} kind="manager" label="Logo del gestore" />
            <LogoBox b={b} kind="company" label="Logo dell'azienda (dati)" />
          </div>
          <Button data-testid="branding-save-button" className="bg-blue-600 hover:bg-blue-700 sm:col-span-2" onClick={save}>Salva landing page</Button>
        </div>
      </Panel>
      <Panel title="Anteprima" testId="branding-preview" action={<div className="flex gap-1">{[["desktop", Monitor], ["phone", Smartphone]].map(([k, I]) => (
        <button key={k} type="button" data-testid={`preview-${k}`} onClick={() => setView(k)} className={`grid h-8 w-8 place-items-center rounded-md border ${view === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-500"}`}><I className="h-4 w-4" /></button>))}</div>}>
        {view === "phone" ? <PhonePreview f={f} b={b} /> : <DesktopPreview f={f} b={b} />}
      </Panel>
    </div>
  );
};
